/**
 * Derived display metadata for character cards: a nationality (label + flag)
 * and a home location. Characters only store a loose `ethnicity` string, so
 * nationality is resolved from surname patterns first, then the ethnicity,
 * then a seeded fallback — always deterministic per character so cards don't
 * change between renders.
 */

export interface CardNationality {
  label: string;
  flag: string;
}

function seed(id: string, salt = 0): number {
  let h = salt;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h;
}

const pick = <T>(pool: T[], h: number): T => pool[h % pool.length]!;

const N = (label: string, flag: string): CardNationality => ({ label, flag });

const POOLS: Record<string, CardNationality[]> = {
  asian: [
    N('Chinese', '🇨🇳'),
    N('Korean', '🇰🇷'),
    N('Japanese', '🇯🇵'),
    N('Thai', '🇹🇭'),
    N('Vietnamese', '🇻🇳'),
  ],
  european: [
    N('French', '🇫🇷'),
    N('Italian', '🇮🇹'),
    N('German', '🇩🇪'),
    N('Spanish', '🇪🇸'),
    N('Greek', '🇬🇷'),
    N('Czech', '🇨🇿'),
    N('Polish', '🇵🇱'),
    N('Portuguese', '🇵🇹'),
  ],
  latina: [
    N('Brazilian', '🇧🇷'),
    N('Colombian', '🇨🇴'),
    N('Mexican', '🇲🇽'),
    N('Argentinian', '🇦🇷'),
    N('Venezuelan', '🇻🇪'),
  ],
  african: [
    N('Nigerian', '🇳🇬'),
    N('Kenyan', '🇰🇪'),
    N('Ghanaian', '🇬🇭'),
    N('Ethiopian', '🇪🇹'),
    N('South African', '🇿🇦'),
  ],
  scandinavian: [
    N('Swedish', '🇸🇪'),
    N('Norwegian', '🇳🇴'),
    N('Danish', '🇩🇰'),
    N('Finnish', '🇫🇮'),
    N('Icelandic', '🇮🇸'),
  ],
  middleEastern: [
    N('Lebanese', '🇱🇧'),
    N('Emirati', '🇦🇪'),
    N('Egyptian', '🇪🇬'),
    N('Turkish', '🇹🇷'),
    N('Moroccan', '🇲🇦'),
  ],
  slavic: [N('Russian', '🇷🇺'), N('Ukrainian', '🇺🇦'), N('Czech', '🇨🇿')],
  mediterranean: [N('Italian', '🇮🇹'), N('Greek', '🇬🇷'), N('Spanish', '🇪🇸')],
  celtic: [N('Irish', '🇮🇪'), N('Scottish', '🇬🇧')],
};

/** ethnicity value (lowercased, normalized) → fixed nationality or pool */
const ETHNICITY_MAP: Record<string, CardNationality | CardNationality[]> = {
  japanese: N('Japanese', '🇯🇵'),
  chinese: N('Chinese', '🇨🇳'),
  'east asian': N('Chinese', '🇨🇳'),
  'east asian inspired': N('Chinese', '🇨🇳'),
  asian: POOLS.asian!,
  european: POOLS.european!,
  latina: POOLS.latina!,
  african: POOLS.african!,
  'african-american': N('American', '🇺🇸'),
  'african american': N('American', '🇺🇸'),
  scandinavian: POOLS.scandinavian!,
  nordic: POOLS.scandinavian!,
  norse: POOLS.scandinavian!,
  finnish: N('Finnish', '🇫🇮'),
  indian: N('Indian', '🇮🇳'),
  'middle-eastern': POOLS.middleEastern!,
  middle_eastern: POOLS.middleEastern!,
  caucasian: N('American', '🇺🇸'),
  american: N('American', '🇺🇸'),
  'polish-american': N('Polish', '🇵🇱'),
  celtic: POOLS.celtic!,
  scottish: N('Scottish', '🇬🇧'),
  'scottish-canadian': N('Canadian', '🇨🇦'),
  british: N('British', '🇬🇧'),
  french: N('French', '🇫🇷'),
  italian: N('Italian', '🇮🇹'),
  roman: N('Italian', '🇮🇹'),
  brazilian: N('Brazilian', '🇧🇷'),
  kenyan: N('Kenyan', '🇰🇪'),
  kushite: N('Sudanese', '🇸🇩'),
  egyptian: N('Egyptian', '🇪🇬'),
  slavic: POOLS.slavic!,
  mediterranean: POOLS.mediterranean!,
  aztec: N('Mexican', '🇲🇽'),
  'native american': N('American', '🇺🇸'),
  'indigenous canadian': N('Canadian', '🇨🇦'),
  inuit: N('Canadian', '🇨🇦'),
  'arctic nomad': N('Icelandic', '🇮🇸'),
  mixed: POOLS.european!,
};

/** Surname → nationality signals, used to refine generic ethnicities. */
const NAME_RULES: { pattern: RegExp; nationality: CardNationality }[] = [
  {
    pattern: /(sson|sen|qvist|ström|strom|berg)\b/i,
    nationality: N('Swedish', '🇸🇪'),
  },
  { pattern: /(ova|eva|ina|skaya|enko)\b/i, nationality: N('Russian', '🇷🇺') },
  { pattern: /(escu|eanu)\b/i, nationality: N('Romanian', '🇷🇴') },
  {
    pattern: /(thatcher|windsor|clarke|bennett)\b/i,
    nationality: N('British', '🇬🇧'),
  },
  { pattern: /\b(murphy|o'|mc|kelly|ryan)/i, nationality: N('Irish', '🇮🇪') },
  { pattern: /\bal[- ]/i, nationality: N('Lebanese', '🇱🇧') },
  {
    pattern: /(costa|silva|santos|oliveira)\b/i,
    nationality: N('Brazilian', '🇧🇷'),
  },
  {
    pattern: /(rossi|moretti|bianchi|ricci|romano|conti)\b/i,
    nationality: N('Italian', '🇮🇹'),
  },
  {
    pattern: /\b(lin|chen|wang|li|zhang|liu|mei)\b/i,
    nationality: N('Chinese', '🇨🇳'),
  },
  { pattern: /\b(kim|park|choi|lee)\b/i, nationality: N('Korean', '🇰🇷') },
  {
    pattern: /(tanaka|sato|yamamoto|nakamura|suzuki|kobayashi)\b/i,
    nationality: N('Japanese', '🇯🇵'),
  },
  { pattern: /(ez|es)\b/i, nationality: N('Spanish', '🇪🇸') },
  {
    pattern: /(dubois|laurent|moreau|lefebvre)\b/i,
    nationality: N('French', '🇫🇷'),
  },
  {
    pattern: /(müller|muller|schmidt|weber|wagner)\b/i,
    nationality: N('German', '🇩🇪'),
  },
];

const FANTASY = /fantasy|demon|draconic|atlantean|aeriean|elf|realm/i;

const GENERIC = new Set([
  'asian',
  'european',
  'latina',
  'african',
  'caucasian',
  'mixed',
  'mediterranean',
  'slavic',
  'celtic',
  'scandinavian',
]);

export function nationalityFor(
  id: string,
  name: string,
  ethnicity?: string | null,
): CardNationality {
  const eth = (ethnicity ?? '').trim().toLowerCase();

  if (FANTASY.test(eth)) return N('Fantasy', '✨');

  const mapped = ETHNICITY_MAP[eth];

  // Specific ethnicity wins outright.
  if (mapped && !Array.isArray(mapped) && !GENERIC.has(eth)) return mapped;

  // Generic (or unknown) ethnicity: let the surname refine it.
  for (const rule of NAME_RULES) {
    if (rule.pattern.test(name)) return rule.nationality;
  }

  if (Array.isArray(mapped)) return pick(mapped, seed(id));
  if (mapped) return mapped;

  return pick(POOLS.european!, seed(id));
}

const LOCATIONS = [
  'Monaco',
  'Dubai',
  'Miami',
  'Los Angeles',
  'Paris',
  'Milan',
  'Ibiza',
  'Mykonos',
  'Tokyo',
  'Seoul',
  'London',
  'New York',
  'Barcelona',
  'Saint-Tropez',
  'Bali',
  'Santorini',
  'Las Vegas',
  'Cannes',
  'Amsterdam',
  'Prague',
] as const;

export function locationFor(id: string): string {
  return pick([...LOCATIONS], seed(id, 7));
}
